import React, { useMemo, useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import ChipComponent from "@/components/ui/ChipComponent";
import "./UIContractRenderer.scss";

interface UIContractMetadata {
    input_type: 'button-menu' | 'button-group' | 'slider' | 'toggle' | 'text-area' | 'text-input' | 'multi-select' | 'chips';
    step_id?: string;
    progress?: number;
    label?: string;
    options?: any;
    min?: number;
    max?: number;
    step?: number;
    default?: any;
    current_value?: any;
    context?: string;
    placeholder?: string;
    multiline?: boolean;
}

interface UIContractRendererProps {
    message: string;
    onAction: (value: string) => void;
    isLastMessage?: boolean;
}

const UIContractRenderer: React.FC<UIContractRendererProps> = ({ message, onAction, isLastMessage }) => {

    const { metadata } = useMemo(() => {
        const uiMetadataRegex = /<ui_metadata>\s*(\{[\s\S]*?\})\s*<\/ui_metadata>/;
        const match = message.match(uiMetadataRegex);

        if (match) {
            try {
                const json = JSON.parse(match[1]);
                return { metadata: json as UIContractMetadata };
            } catch (e) {
                console.error("Failed to parse ui_metadata", e);
            }
        }

        return { metadata: null };
    }, [message]);

    const [inputValue, setInputValue] = useState<string>('');

    useEffect(() => {
        if (metadata?.input_type === 'slider' && metadata.default !== undefined) {
            setInputValue(String(metadata.default));
        } else if ((metadata?.input_type === 'text-area' || metadata?.input_type === 'text-input')) {
            setInputValue(String(metadata.current_value || metadata.default || ''));
        }
    }, [metadata]);

    if (!metadata) return null;

    // Only render interactive components if it's the last message to avoid historical confusion
    if (!isLastMessage) return null;

    const renderComponent = () => {
        switch (metadata.input_type) {
            case 'button-menu':
            case 'button-group':
                return (
                    <div className="ui-contract-buttons flex flex-wrap gap-2 mt-4">
                        {metadata.label && <p className="w-full text-sm font-medium text-neutral-400 mb-1">{metadata.label}</p>}
                        {metadata.options?.map((opt: any) => (
                            <Button
                                key={opt.id}
                                variant={opt.variant === 'danger' ? 'destructive' : opt.variant === 'primary' ? 'default' : 'secondaryOutline'}
                                size="sm"
                                onClick={() => onAction(opt.id)}
                                className="transition-all duration-200 hover:scale-[1.02]"
                            >
                                {opt.label}
                            </Button>
                        ))}
                    </div>
                );
            case 'slider':
                return (
                    <div className="ui-contract-slider space-y-4 mt-6 p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                        <div className="flex justify-between items-center">
                            <Label className="text-sm font-medium text-neutral-300">{metadata.label || "Select Value"}</Label>
                            <span className="text-xs font-mono text-primary bg-primary/10 px-2 py-0.5 rounded">
                                {inputValue}
                            </span>
                        </div>
                        <input
                            type="range"
                            defaultValue={metadata.default || 0}
                            max={metadata.max || 100}
                            min={metadata.min || 0}
                            value={inputValue}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setInputValue(e.target.value);
                            }}
                            onMouseUp={(e: any) => onAction(e.target.value)}
                            onTouchEnd={(e: any) => onAction(e.target.value)}
                            className="w-full h-1.5 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                    </div>
                );
            case 'text-area':
            case 'text-input':
                return (
                    <div className="ui-contract-text mt-4 space-y-3 p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                        {metadata.label && <Label className="text-sm font-medium text-neutral-300">{metadata.label}</Label>}
                        {metadata.input_type === 'text-area' ? (
                            <textarea
                                className="w-full min-h-[100px] bg-neutral-900 border border-neutral-700 rounded-lg p-3 text-sm text-neutral-200 placeholder:text-neutral-500 focus:ring-1 focus:ring-primary/50 outline-none resize-none"
                                placeholder={metadata.placeholder || "Enter text here..."}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                        onAction(inputValue);
                                    }
                                }}
                            />
                        ) : (
                            <input
                                type="text"
                                className="w-full h-10 bg-neutral-900 border border-neutral-700 rounded-lg px-3 text-sm text-neutral-200 placeholder:text-neutral-500 focus:ring-1 focus:ring-primary/50 outline-none"
                                placeholder={metadata.placeholder || "Enter text here..."}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        onAction(inputValue);
                                    }
                                }}
                            />
                        )}
                        <div className="flex justify-end gap-2">
                            <span className="text-[10px] text-neutral-500 mt-auto mr-auto">
                                {metadata.input_type === 'text-area' ? "Press Cmd+Enter to submit" : "Press Enter to submit"}
                            </span>
                            <Button
                                size="sm"
                                variant="default"
                                onClick={() => onAction(inputValue)}
                                disabled={!inputValue.trim()}
                            >
                                Submit
                            </Button>
                        </div>
                    </div>
                );
            case 'toggle':
                return (
                    <div className="ui-contract-toggle flex items-center justify-between mt-4 p-4 rounded-xl bg-neutral-900/50 border border-neutral-800">
                        <Label className="text-sm font-medium text-neutral-300">{metadata.label || "Enable Option"}</Label>
                        <input
                            type="checkbox"
                            className="w-10 h-5 bg-neutral-800 rounded-full appearance-none relative cursor-pointer checked:bg-primary transition-colors before:content-[''] before:absolute before:w-4 before:h-4 before:bg-white before:rounded-full before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5"
                            onChange={(e) => {
                                const checked = e.target.checked;
                                const val = metadata.options?.[checked.toString()] || (checked ? 'y' : 'n');
                                onAction(val);
                            }}
                        />
                    </div>
                );
            case 'multi-select':
            case 'chips':
                return (
                    <div className="ui-contract-chips mt-4 space-y-2">
                        {metadata.label && <p className="text-sm font-medium text-neutral-400">{metadata.label}</p>}
                        <div className="flex flex-wrap gap-2">
                            {metadata.options?.map((opt: any) => (
                                <button
                                    key={opt.id}
                                    onClick={() => onAction(opt.id)}
                                    className="focus:outline-none"
                                >
                                    <ChipComponent
                                        text={opt.label}
                                        variant="default"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="ui-contract-container animate-in fade-in slide-in-from-bottom-2 duration-500">
            {renderComponent()}
        </div>
    );
};

export default UIContractRenderer;
